import { HsMatrix4 } from '../objects/matrix4.class';
import { HsVector3 } from '../objects/vector3.class';

export class HsSphere {
  public readonly center: HsVector3;
  public radius: number;

  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp2: HsVector3 = HsVector3.makeZeroVector();

  constructor(x: number, y: number, z: number, radius: number) {
    this.center = new HsVector3(x, y, z);
    this.radius = radius;
  }

  /**
   * Sets set.
   * @param center center.
   * @param radius radius.
   * @returns Result value.
   */
  public set(center: HsVector3, radius: number): HsSphere {
    this.center.copy(center);
    this.radius = radius;
    return this;
  }

  /**
   * Copies copy.
   * @returns Result value.
   */
  public copy(other: HsSphere): HsSphere {
    this.center.copy(other.center);
    this.radius = other.radius;
    return this;
  }

  /**
   * Handles apply matrix4.
   * @param matrix matrix.
   * @returns Result value.
   */
  public applyMatrix4(matrix: HsMatrix4): HsSphere {
    this.center.applyMatrix4(matrix.values);
    this.radius = this.radius * HsMatrix4.getMaxScaleOnAxis(matrix);
    return this;
  }

  /**
   * Sets from box.
   * @param box box.
   * @returns Result value.
   */
  public setFromBox(boxMin: HsVector3, boxMax: HsVector3): HsSphere {
    const center: HsVector3 = HsSphere.#vector3Temp1;
    const size: HsVector3 = HsSphere.#vector3Temp2;
    HsVector3.average(boxMin, boxMax, center);
    const radius: number = size.subtractVectors(boxMin, boxMax).length() * 0.5;
    return this.set(center, radius);
  }

  /**
   * Builds empty.
   * @returns Result value.
   */
  public static makeEmpty(): HsSphere {
    return new HsSphere(0, 0, 0, 0);
  }

  /**
   * Builds sphere from bounding box.
   * @param box box.
   * @returns Result value.
   */
  public static makeSphereFromBoundingBox(boxMin: HsVector3, boxMax: HsVector3): HsSphere {
    return new HsSphere(0, 0, 0, 0).setFromBox(boxMin, boxMax);
  }

  /**
   * Handles distance.
   * @param sphere sphere.
   * @param point point.
   * @returns Result value.
   */
  public static distance(sphere: HsSphere, point: HsVector3): number {
    return point.length() - sphere.radius;
  }

  /**
   * Builds copy.
   * @param source source.
   * @returns Result value.
   */
  public static makeCopy(source: HsSphere): HsSphere {
    return new HsSphere(source.center.values[0], source.center.values[1], source.center.values[2], source.radius);
  }
}
